import {
  absentReasonText,
  conductAxisMeta,
  conductValueLabel,
  originSentence,
} from './conduct-labels';

import type { ConductAxis, ConductAxisResolution, ConductOriginLevel } from './conduct-types';
import type { ConductProfileLevels } from './ConductAxisField';

/**
 * Fase 2 «Conduta e Contexto» — um eixo em modo LEITURA.
 *
 * Existe porque modo-leitura nesta casa é TEXTO, não input desabilitado: o editor de Persona
 * tem um guard executável (`personas-pr3.spec.tsx`) que assere
 * `document.querySelector('select') === null` sem a cap de autoria, e as `Section*Read`
 * irmãs (`SectionBehaviorRead`, `SectionVoiceRead`) todas renderizam `<p>`. Um `<select
 * disabled>` passaria o olho e quebraria o contrato — e o contrato está certo: para quem não
 * pode editar, um seletor cinza é ruído que sugere uma ação que não existe.
 *
 * A informação NÃO é reduzida por ser leitura: o valor efetivo, a origem e a inércia
 * continuam visíveis. É a mesma honestidade do modo de edição, sem a affordance de escrita.
 */
export function ConductAxisReadout({
  axis,
  level,
  resolution,
  value,
  levels,
}: {
  readonly axis: ConductAxis;
  readonly level: ConductOriginLevel;
  readonly resolution: ConductAxisResolution | null;
  /** A opinião DESTE nível. `null` = não opinou (herda). */
  readonly value: string | null;
  readonly levels?: ConductProfileLevels | undefined;
}): JSX.Element {
  const meta = conductAxisMeta(axis);

  const originName =
    resolution === null || levels === undefined || resolution.origin === 'platform'
      ? null
      : (levels[resolution.origin]?.name ?? null);

  const selfPresence = levels !== undefined && level !== 'platform' ? levels[level] : undefined;
  const inertReason =
    selfPresence !== undefined && !selfPresence.present
      ? absentReasonText(selfPresence.absentReason)
      : null;

  return (
    <div className="pe-field">
      <span className="pe-field__label">{meta.label}</span>
      {/* O valor EFETIVO — o que a IA faz hoje. Nunca um espaço vazio. */}
      <p className="pe-read-value" data-testid={`conduct-readout-${axis}`}>
        {resolution === null ? '—' : conductValueLabel(axis, resolution.value)}
      </p>
      {/* De onde vem. */}
      {resolution !== null ? (
        <p className="pe-read-value pe-read-value--muted">
          {value === null
            ? originSentence(resolution.origin, level, originName)
            : 'Definido neste nível.'}
        </p>
      ) : null}
      {inertReason !== null ? (
        <p className="pe-read-value pe-read-value--muted" data-testid={`conduct-inert-${axis}`}>
          {inertReason}
        </p>
      ) : null}
    </div>
  );
}
